/**
 * Alpine component: scroll-down hint for the compact profile side panel.
 *
 * Shows a pill at the bottom of the panel until the user has scrolled close
 * to the end. Also hides whenever an ad container is inside the pill's
 * danger zone so the hint never overlays an ad.
 *
 * Usage:
 *   <div class="side-panel-profile overflow-y-auto"
 *        x-data="compactProfileScrollHint()"
 *        x-init="init($el)">
 *     ... content ...
 *     <div data-scroll-hint-ad>ad</div>
 *     ... more content ...
 *     <button x-show="visible" @click="scrollDown()">More ↓</button>
 *   </div>
 */
// Height of the danger zone at the bottom of the scroll viewport. If an ad
// intersects this zone the pill is hidden so it never overlays the ad.
const DANGER_ZONE_PX = 160;
const AT_BOTTOM_PX = 80;

export function compactProfileScrollHint() {
    return {
        visible: false,
        _container: null,
        _ads: [],

        init(container) {
            this._container = container;
            this._ads = Array.from(container.querySelectorAll('[data-scroll-hint-ad]'));

            const onScroll = () => this._recompute();
            container.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onScroll, { passive: true });

            // Recompute after ads load / content resizes.
            if ('ResizeObserver' in window) {
                const ro = new ResizeObserver(() => this._recompute());
                ro.observe(container);
                this._ads.forEach((ad) => ro.observe(ad));
            }

            // Defer initial pass so content has laid out.
            requestAnimationFrame(() => this._recompute());
        },

        _recompute() {
            const el = this._container;
            if (!el) {
                this.visible = false;
                return;
            }

            const overflow = el.scrollHeight - el.clientHeight;
            if (overflow <= 0) {
                this.visible = false;
                return;
            }

            const atBottom = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - AT_BOTTOM_PX);
            if (atBottom) {
                this.visible = false;
                return;
            }

            // Hide if any ad currently intersects the bottom danger zone.
            const containerRect = el.getBoundingClientRect();
            const dangerTop = containerRect.bottom - DANGER_ZONE_PX;
            const dangerBottom = containerRect.bottom;

            for (const ad of this._ads) {
                const r = ad.getBoundingClientRect();
                if (r.bottom > dangerTop && r.top < dangerBottom) {
                    this.visible = false;
                    return;
                }
            }

            this.visible = true;
        },

        scrollDown() {
            const el = this._container;
            if (!el) return;
            el.scrollBy({ top: Math.round(el.clientHeight * 0.8), behavior: 'smooth' });
        },
    };
}
